import { Box, SxProps } from '@mui/material';
import { t } from 'i18next';
import { Outlet } from 'react-router-dom';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { bind } from '../../../common/utils/decorators';
import { ReportExportState } from '../../../exports/triggers/states/ReportExportState';
import { PATHS } from '../../../routing/routes';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { ReportDone, ReportFailed } from '../../common/models/Report';
import { ColumnPlaceholder } from '../common/components/ColumnPlaceholder';
import { InitialInstancePayload, ReportIssuesContextType } from '../common/contexts/ReportIssuesContext';
import { IssuesListSkeleton } from '../components/skeletons/IssuesListSkeleton';
import { IssuesListController } from './IssuesListController';

type Props = WithRouterProps<ReportOutletContextType> & {};

type State = BaseState & {
  initialIssuesLoaded: boolean;
  initialIssueLoaded: boolean;
  initialInstanceLoaded: boolean;

  initialInstance?: InitialInstancePayload;
};

// Set a height to the container to allow internal scrolling of individual
// columns. The height is set so that it fills completely and does not
// overflow the SecondaryNav content when it is collapsed.
//
// Screen height - Navbar height - Tabs height - SecondaryNav content padding
export const REPORT_ISSUES_CONTAINER_HEIGHT = 'calc(100vh - var(--main-tabs-height) - var(--secondary-tabs-height) - 2rem)';

// Applying `overflowY` to a container also clips any `boxShadow` within it,
// no matter how nested it is in the DOM structure. To prevent this,
// expand the elements with a negative margin, keep the correct size with
// a positive padding, and adjust the height to account for the extra
// space given by the negative margin.
// Implemented as a custom CSS class applied from the main container of
// this controller, so nested controllers are not affected if ever used
// individually.
const SCROLLABLE_BOX_SHADOW_CHILDREN_SX: SxProps = {
  '& .overflowing-box-shadow': {
    m: '-4px',
    p: '4px',
    height: 'calc(100% + 8px)',
    overflowY: 'scroll',
  },
};

const FILTER_TOGGLE_SEARCH_PARAM = 'filtered';

class Controller extends BaseController<Props, State> {
  constructor(props: Props, context: unknown) {
    super(props, context);

    const { issueNumber, instanceNumber } = this.getPathParams();

    this.state = {
      ...super.getBaseState(),
      // Issues are not loaded when the component mounts
      initialIssuesLoaded: false,

      // An Issue needs to be loaded only if we are on the `report/:reportId/:issueNumber`
      // or `report/:reportId/:issueNumber/:instanceNumber` route
      initialIssueLoaded: !issueNumber,

      // An Instance needs to be loaded only if we are on the
      // `report/:reportId/:issueNumber/:instanceNumber` route
      initialInstanceLoaded: !instanceNumber,
    };
  }

  render() {
    const { report } = this.props.context;
    const { initialIssuesLoaded, initialIssueLoaded, initialInstanceLoaded, initialInstance } = this.state;
    const { reportId, issueNumber } = this.getPathParams();

    const reportReady = report instanceof ReportDone && report.getIssuesCount() > 0;
    const initialLoad = !(reportReady && initialIssuesLoaded && initialIssueLoaded && initialInstanceLoaded);
    const canLoadIssues = reportReady && initialInstanceLoaded;
    const includeFilteredRules = this.hasFilterSearchParam();

    const exportState = ReportExportState.getInstance();
    if (exportState.report.value) {
      exportState.setIncludeFilteredRules(includeFilteredRules);
    }

    const context: ReportIssuesContextType = {
      reportReady,
      initialIssuesLoaded: reportReady && initialIssuesLoaded,
      initialIssueLoaded: reportReady && initialIssueLoaded,
      initialInstanceLoaded: reportReady && initialInstanceLoaded,
      initialInstance,
      includeFilteredItems: includeFilteredRules,
      onIssuesLoaded: this.onIssuesLoaded,
      onIssueLoaded: this.onIssueLoaded,
      onInstanceLoaded: this.onInstanceLoaded,
    };

    if (report instanceof ReportFailed || (report instanceof ReportDone && report.getIssuesCount() === 0)) {
      return <ColumnPlaceholder title={t('noIssuesPlaceholder.title')} subtitle={t('noIssuesPlaceholder.subtitle')} loading={false} initialLoad={false} />;
    }

    return this.renderBase(
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(24, 1fr)',
          columnGap: 2,
          height: REPORT_ISSUES_CONTAINER_HEIGHT,
          ...SCROLLABLE_BOX_SHADOW_CHILDREN_SX,
        }}
      >
        <Box className="overflowing-box-shadow" sx={{ gridColumn: { xl: 'span 6', xs: 'span 12' } }}>
          {canLoadIssues ? (
            <IssuesListController
              reportId={reportId}
              selectedIssueNumber={issueNumber}
              onIssuesLoaded={context.onIssuesLoaded}
              filterToggleValue={includeFilteredRules}
              onFilterToggled={this.onFilterToggleChanged}
              onIssueFiltered={this.onIssueFiltered}
            />
          ) : (
            <IssuesListSkeleton />
          )}
        </Box>
        <Box className="overflowing-box-shadow" sx={{ gridColumn: { xl: 'span 18', xs: 'span 12' } }}>
          {issueNumber ? (
            <Outlet context={context} />
          ) : (
            <ColumnPlaceholder
              title={t('selectIssuePlaceholder.title')}
              subtitle={t('selectIssuePlaceholder.subtitle')}
              loading={initialLoad}
              initialLoad={initialLoad}
            />
          )}
        </Box>
      </Box>
    );
  }

  private getPathParams() {
    return {
      reportId: this.parseNumericIds(['reportId']).reportId,
      issueNumber: Number.parseId(this.props.params['issueId']),
      instanceNumber: Number.parseId(this.props.params['instanceId']),
    };
  }

  @bind
  private onIssuesLoaded() {
    this.setState({ initialIssuesLoaded: true });
  }

  @bind
  private onIssueLoaded() {
    if (!this.state.initialIssueLoaded) {
      this.setState({ initialIssueLoaded: true });
    }
  }

  @bind
  private onInstanceLoaded(selectedInstance: InitialInstancePayload) {
    if (!this.state.initialInstanceLoaded) {
      this.setFilterSearchParam(!selectedInstance.keptByFilters);
      this.setState({ initialInstanceLoaded: true, initialInstance: selectedInstance });
    }
  }

  @bind
  private onIssueFiltered() {
    this.redirectToBasePath();
  }

  @bind
  private onFilterToggleChanged(checked: boolean) {
    // When the filter toggle changes there is no guarantee that the currently selected
    // issue or instance will exist in the updated result.
    // Redirect users to the base issues view to avoid inconsistent display
    this.setFilterSearchParam(checked);
    this.redirectToBasePath();
  }

  private hasFilterSearchParam() {
    return this.props.searchParams.has(FILTER_TOGGLE_SEARCH_PARAM, 'true');
  }

  private setFilterSearchParam(value: boolean) {
    this.props.setSearchParams(value ? { [FILTER_TOGGLE_SEARCH_PARAM]: 'true' } : {}, { replace: true });
  }

  private redirectToBasePath() {
    const { reportId } = this.getPathParams();
    this.props.navigate(UrlHelper.generatePath(PATHS.REPORT, { reportId: reportId.toString() }, { keepQueryParams: true }), { replace: true });
  }
}

export const ReportIssuesController = withRouter<ReportOutletContextType>(Controller);
